import React, { useEffect, useMemo, useState } from "react";
import { EmployeeFirstLoginRes } from "../../../services/dto/response/user-response-dto";
import { Group, groupTypes } from "../../../types";
import {
  Checkbox,
  FormControlLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Switch,
  TextField,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { ButtonForm } from "../button-form";
import { EditEmployeeFirstLogin } from "../../../services/dto/request/user-request-dto";
import { UserService } from "../../../services/user-service";
import { useToast } from "../../../context/toast-context";
import { BranchService } from "../../../services/branch-service";
import { QrDisplayConfigService } from "../../../services/qr-display-config-service";
import { QrDisplayConfig } from "../../../types/qr-display-config";
import { QrDisplayConfigSelect } from "../../teams/qr-display-config-select";

interface EditEmployeeFormProps {
  employee: EmployeeFirstLoginRes;
  groups: Group[];
  equalSplitTip: boolean;
  updateEmployees: (updatedEmployees: any) => void;
  branchId: number;
  setIsModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  fetchAll?: boolean;
  onLeaveConfig?: boolean;
}

export function EditEmployeeForm({
  employee,
  groups,
  updateEmployees,
  equalSplitTip,
  branchId,
  setIsModalOpen,
  fetchAll = false,
  onLeaveConfig = false,
}: EditEmployeeFormProps) {
  const { t } = useTranslation();

  const [editedEmployee, setEditedEmployee] = useState({
    name: employee.name ?? "",
    email: employee.email ?? "",
    groupsIds:
      employee.userGroups.length && employee.userGroups[0].id !== null
        ? employee.userGroups.map((group) => group.id)
        : [],
    hasQR: employee.useQR,
    groupTypes:
      employee.groupsTypes.length && employee.groupsTypes[0].id !== null
        ? employee.groupsTypes.map((group) => group.typeId)
        : [],
    onLeave: employee.onLeave ?? false,
    qrDisplayConfigId: employee.qrDisplayConfigId ?? "",
  });

  const [error, setError] = useState<string>("");
  const [loading, setIsLoading] = useState(false);
  const { showToast } = useToast();
  const userService = useMemo(() => new UserService(), []);
  const branchService = useMemo(() => new BranchService(), []);
  const qrDisplayConfigService = useMemo(() => new QrDisplayConfigService(), []);
  const [qrConfigs, setQrConfigs] = useState<QrDisplayConfig[]>([]);

  const handleGroupChange = (value: number[]) => {
    setEditedEmployee((prev) => ({
      ...prev,
      groupsIds: value,
    }));
    setError("");
  };

  const qrGroupId = groups.find((group) => group.name === "QR")?.groupId;

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;
    qrDisplayConfigService.getConfigs(
      (data) => {
        if (!signal.aborted) {
          setQrConfigs(data);
          setEditedEmployee((prev) => {
            if (
              typeof prev.qrDisplayConfigId === "number" &&
              data.some((config) => config.qrDisplayConfigId === prev.qrDisplayConfigId)
            ) {
              return prev;
            }
            return { ...prev, qrDisplayConfigId: "" };
          });
        }
      },
      (errorMessage) => {
        if (!signal.aborted) {
          showToast(errorMessage, "error");
        }
      },
      branchId
    );
    return () => {
      controller.abort();
    };
  }, [qrDisplayConfigService, showToast, branchId]);

  const handleGroupTypeChange = (value: number[]) => {
    setEditedEmployee((prev) => ({
      ...prev,
      groupTypes: value,
      hasQR: value.includes(2),
      groupsIds: value.includes(1) ? [...prev.groupsIds] : [],
    }));
    setError("");
  };

  const handleInputChange = (
    field: keyof typeof editedEmployee,
    value: string | null
  ) => {
    setEditedEmployee({
      ...editedEmployee,
      [field]: value === "-1" ? null : value,
    });
    setError("");
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);

    const params: EditEmployeeFirstLogin = {
      Email: editedEmployee.email,
      Groups: editedEmployee.groupsIds.filter((id) => id !== null),
      Name: editedEmployee.name,
      UseQR:
        editedEmployee.hasQR ||
        (!equalSplitTip && editedEmployee.groupTypes.includes(2)),
      UseGroupTypeQr: editedEmployee.groupTypes.includes(2),
      UserId: employee.userId,
      BranchId: branchId,
      OnLeave: onLeaveConfig ? editedEmployee.onLeave : false,
      QrDisplayConfigId:
        typeof editedEmployee.qrDisplayConfigId === "number"
          ? editedEmployee.qrDisplayConfigId
          : undefined,
    };

    await userService.editEmployeeFirstLogin(
      params,
      async () => {
        if (fetchAll) {
          await userService.getEmployees(
            {
              limit: 100,
              offset: 0,
            },
            (data) => {
              updateEmployees(data.users);
              setIsLoading(false);
              setIsModalOpen(false);
            },
            (error) => showToast(error, "error")
          );
        } else {
          await branchService.getBranchesInformation(
            {
              limit: 100,
              offset: 0,
              branchid: branchId.toString(),
            },
            (data) => {
              updateEmployees(data[0].employees);
              setIsLoading(false);
              setIsModalOpen(false);
            },
            (error) => {
              showToast(error, "error");
              setIsLoading(false);
            }
          );
        }
        setIsLoading(false);
        setIsModalOpen(false);
        showToast(t("employee.edit.success"), "success");
      },
      (error) => {
        showToast(error, "error");
        setIsLoading(false);
      }
    );
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="flex justify-center items-center px-6 gap-4">
        <div className="flex items-center w-full justify-between">
          <TextField
            type="text"
            fullWidth
            label={t("employee.create.fullname")}
            variant="standard"
            required
            value={editedEmployee.name}
            onInput={(e) => {
              const input = e.target as HTMLInputElement;
              let value = input.value;

              value = value.replace(/[^a-zA-ZÀ-ÿ\s']/g, ""); // No permitimos numeros o simbolos

              input.value = value;
            }}
            onChange={(e) => handleInputChange("name", e.target.value)}
            sx={{
              "& .MuiInput-root": {
                color: "#000",
                fontFamily: "Montserrat",
                "&:before": {
                  borderBottom: "1px solid #838383",
                },
                "&:hover:not(.Mui-disabled):before": {
                  borderBottomColor: "#898989",
                },
              },
              "& label.Mui-focused": {
                color: "#7e7e7e",
              },
              "& .MuiInputLabel-standard": {
                color: "#000",
                fontWeight: "400",
                fontFamily: "Montserrat",
              },
              "& .MuiInput-underline:after": {
                borderBottomColor: "#838383",
              },
            }}
          />
        </div>

        <div className="flex items-center w-full justify-between">
          <TextField
            fullWidth
            type="email"
            label={t("employee.create.email")}
            disabled={employee.isVerified}
            required
            variant="standard"
            value={editedEmployee.email}
            onChange={(e) => handleInputChange("email", e.target.value)}
            sx={{
              "& .MuiInput-root": {
                color: "#000",
                fontFamily: "Montserrat",
                "&:before": {
                  borderBottom: "1px solid #838383",
                },
                "&:hover:not(.Mui-disabled):before": {
                  borderBottomColor: "#898989",
                },
              },
              "& label.Mui-focused": {
                color: "#7e7e7e",
              },
              "& .MuiInputLabel-standard": {
                color: "#000",
                fontWeight: "400",
                fontFamily: "Montserrat",
              },
              "& .MuiInput-underline:after": {
                borderBottomColor: "#838383",
              },
            }}
          />
        </div>

        {!equalSplitTip && (
          <>
            <div className="flex mt-6 items-center w-full max-w-[200px] justify-between">
              <Select
                sx={{
                  paddingX: 2,
                  paddingY: 1,
                  margin: 0,
                  fontStyle: "normal",
                  fontFamily: "Montserrat",
                  fontSize: "1rem",
                  height: "100%",
                  width: "200px",
                  minWidth: "100%",
                  maxWidth: "230px",
                  borderRadius: "9999px",
                  border: "1px solid",
                  borderColor: "#dfdfdf",
                  backgroundColor: "transparent",
                  outline: "none",
                  "&:focus": {
                    outlineColor: "primary.main",
                  },
                  "& .MuiSelect-select": {
                    padding: 0,
                    display: "block",
                    minWidth: 0,
                  },
                  "&.MuiInputBase-root.MuiOutlinedInput-root": {
                    padding: "4px 8px",
                  },
                  "& .MuiSelect-select.MuiInputBase-input.MuiOutlinedInput-input.Mui-disabled":
                    {
                      WebkitTextFillColor: "#dfdfdf",
                      opacity: "70%",
                    },

                  fontWeight: "400",
                }}
                value={editedEmployee.groupTypes}
                required
                multiple
                input={<OutlinedInput />}
                displayEmpty
                onChange={(e) =>
                  handleGroupTypeChange(e.target.value as number[])
                }
                renderValue={(selected) => {
                  if (selected.length === 0) {
                    return (
                      <span className="block truncate ">
                        {t("select.default")}
                      </span>
                    );
                  }
                  const selectedTypeNames = selected
                    .map((typeId) => {
                      const type = groupTypes.find(
                        (group) => group.groupTypeId === typeId
                      );
                      return type ? type.name : "";
                    })
                    .filter((name) => name !== "");

                  return selectedTypeNames.join(", ");
                }}
              >
                <MenuItem
                  sx={{
                    fontFamily: "Montserrat",
                  }}
                  disabled
                  value=""
                >
                  <span>{t("select.default")}</span>
                </MenuItem>
                {groupTypes.map((type) => (
                  <MenuItem
                    sx={{
                      fontFamily: "Montserrat",
                    }}
                    key={type.groupTypeId}
                    value={type.groupTypeId}
                  >
                    {type.name}
                  </MenuItem>
                ))}
              </Select>
            </div>
            <div className="flex mt-6 items-center w-full max-w-[200px] justify-between">
              <Select
                sx={{
                  paddingX: 2,
                  paddingY: 1,
                  margin: 0,
                  fontStyle: "normal",
                  fontFamily: "Montserrat",
                  fontSize: "1rem",
                  height: "100%",
                  width: "230px",
                  minWidth: "100%",
                  maxWidth: "230px",
                  borderRadius: "9999px",
                  border: "1px solid",
                  borderColor: "#838383",
                  backgroundColor: "transparent",
                  outline: "none",
                  "&:focus": {
                    outlineColor: "primary.main",
                  },
                  "& .MuiSelect-select": {
                    padding: 0,
                    display: "block",
                    minWidth: 0,
                  },
                  "&.MuiInputBase-root.MuiOutlinedInput-root": {
                    padding: "4px 8px",
                  },

                  fontWeight: "400",
                }}
                required
                multiple
                input={<OutlinedInput />}
                disabled={!editedEmployee.groupTypes.includes(1)}
                value={editedEmployee.groupsIds.filter(
                  (group) => group !== qrGroupId
                )}
                displayEmpty
                onChange={(e) => handleGroupChange(e.target.value as number[])}
                renderValue={(selected) => {
                  if (selected.length === 0) {
                    return (
                      <span className="block truncate ">
                        {t("select.group")}
                      </span>
                    );
                  }

                  return selected
                    .map(
                      (groupId) =>
                        groups.find((group) => group.groupId === groupId)?.name
                    )
                    .filter((name) => name)
                    .join(", ");
                }}
              >
                <MenuItem
                  sx={{
                    fontFamily: "Montserrat",
                  }}
                  disabled
                  value=""
                >
                  <span>{t("employee.create.group")}</span>
                </MenuItem>
                {groups
                  .filter((group) => group.name !== "QR")
                  .map((group) => (
                    <MenuItem
                      sx={{
                        fontFamily: "Montserrat",
                      }}
                      key={group.name}
                      value={group.groupId}
                    >
                      {group.name}
                    </MenuItem>
                  ))}
              </Select>
            </div>
          </>
        )}
        {equalSplitTip && (
          <div className="flex mt-6 items-center">
            <FormControlLabel
              sx={{
                "& .MuiTypography-root": {
                  color: "#898989",
                  paddingLeft: "2px",
                  fontFamily: "Montserrat",
                },
              }}
              control={
                <Checkbox
                  sx={{
                    color: "#898989",
                    padding: 0,
                    "&.Mui-checked": {
                      color: "#EF0BB8",
                    },
                    "& .MuiSvgIcon-root": {
                      fontSize: 24,
                      margin: 0,
                      padding: 0,
                    },
                  }}
                  onChange={(e) => {
                    setError("");
                    if (!editedEmployee.groupTypes.includes(2)) {
                      setEditedEmployee((prev) => ({
                        ...prev,
                        hasQR: e.target.checked,
                      }));
                    }
                  }}
                  checked={
                    editedEmployee.hasQR ||
                    (!equalSplitTip && editedEmployee.groupTypes.includes(2))
                  }
                />
              }
              label="QR"
            />
          </div>
        )}
      </div>
      {onLeaveConfig && (
        <div className="px-6 pt-4">
          <FormControlLabel
            sx={{
              "& .MuiTypography-root": {
                fontFamily: "Montserrat",
                color: "#000",
              },
              "& .MuiFormControlLabel-label.Mui-disabled": {
                color: "#000",
              },
            }}
            control={
              <Switch
                checked={editedEmployee.onLeave}
                onChange={(e) =>
                  setEditedEmployee((prev) => ({
                    ...prev,
                    onLeave: e.target.checked,
                  }))
                }
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked+.MuiSwitch-track": {
                    bgcolor: "#EF0BB8",
                  },
                  "& .MuiSwitch-track": {
                    bgcolor: "#898989",
                  },
                  "& .MuiSwitch-thumb": {
                    bgcolor: "#EF0BB8",
                  },
                }}
              />
            }
            label={t("employee.edit.leave")}
          />
        </div>
      )}

      {error && <div className="mt-4 text-red text-center">{error}</div>}

      {qrConfigs.length > 0 && (
        <div className="flex flex-col items-start gap-1 px-6 mt-8">
          <span className="text-sm text-[#2d2f32]">{t("employee.qr-config.label")}</span>
          <QrDisplayConfigSelect
            configs={qrConfigs}
            value={
              typeof editedEmployee.qrDisplayConfigId === "number"
                ? editedEmployee.qrDisplayConfigId
                : ""
            }
            placeholder={t("employee.qr-config.select")}
            onChange={(qrDisplayConfigId) =>
              setEditedEmployee((prev) => ({
                ...prev,
                qrDisplayConfigId,
              }))
            }
          />
        </div>
      )}

      <div className="flex justify-center mt-12 mx-6">
        <div className="w-full max-w-sm">
          <ButtonForm loading={loading}>{t("employee.edit.submit")}</ButtonForm>
        </div>
      </div>
    </form>
  );
}
